import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Plus, Search } from 'lucide-react';

import { MedicalOption, medicalCategories } from '../../data/medicalData';

/**
 * @component MedicalSelector
 * @description Selector médico con búsqueda, categorías y opción "Otro"
 */

interface MedicalSelectorProps {
  options: MedicalOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  label?: string;
  multiple?: boolean;
  allowCustom?: boolean;
  customPlaceholder?: string;
  showCategories?: boolean;
  className?: string;
  error?: string;
}

const MedicalSelector: React.FC<MedicalSelectorProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  label,
  multiple = true,
  allowCustom = true,
  customPlaceholder = 'Especificar otro...',
  showCategories = true,
  className = '',
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomInput(false);
        setCustomValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrar opciones por búsqueda
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Agrupar opciones por categoría
  const groupedOptions = showCategories ? 
    filteredOptions.reduce((groups, option) => {
      const category = option.category || 'otros';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(option);
      return groups;
    }, {} as Record<string, MedicalOption[]>) :
    { all: filteredOptions };

  const handleOptionSelect = (optionValue: string) => {
    if (optionValue === 'otro' && allowCustom) {
      setShowCustomInput(true);
      setIsOpen(false);
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }

    if (multiple) {
      const newValue = value.includes(optionValue)
        ? value.filter(v => v !== optionValue)
        : [...value, optionValue];
      onChange(newValue);
    } else {
      onChange([optionValue]);
      setIsOpen(false);
    }
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      const newValue = multiple ? [...value, customValue.trim()] : [customValue.trim()];
      onChange(newValue);
      setCustomValue('');
      setShowCustomInput(false);
    }
  };

  const handleRemoveValue = (valueToRemove: string) => {
    onChange(value.filter(v => v !== valueToRemove));
  };

  const getSelectedLabels = () => {
    return value.map(v => {
      const option = options.find(opt => opt.value === v);
      return option ? option.label : v;
    });
  };

  const getCategoryLabel = (category: string, type: 'diseases' | 'allergies' | 'medications') => {
    return medicalCategories[type]?.[category as keyof typeof medicalCategories[typeof type]] || category;
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Selected Values */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {getSelectedLabels().map((label, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-800 text-sm rounded-md"
            >
              {label}
              <button
                type="button"
                onClick={() => handleRemoveValue(value[index])}
                className="text-primary-600 hover:text-primary-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Custom Input */}
      {showCustomInput && (
        <div className="flex gap-2 mb-2">
          <input
            ref={inputRef}
            type="text"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCustomSubmit()}
            placeholder={customPlaceholder}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            type="button"
            onClick={handleCustomSubmit}
            className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setShowCustomInput(false);
              setCustomValue('');
            }}
            className="px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Selector */}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full px-3 py-2 text-left bg-white border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            error ? 'border-red-300' : 'border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={value.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
              {value.length === 0 ? placeholder : `${value.length} seleccionado${value.length > 1 ? 's' : ''}`}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {/* Search */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Options */}
            <div className="py-1">
              {Object.entries(groupedOptions).map(([category, categoryOptions]) => (
                <div key={category}>
                  {showCategories && category !== 'all' && categoryOptions.length > 0 && (
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                      {getCategoryLabel(category, 'diseases')}
                    </div>
                  )}
                  {categoryOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleOptionSelect(option.value)}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                        value.includes(option.value) ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                      }`}
                    >
                      <span>{option.label}</span>
                      {value.includes(option.value) && (
                        <div className="w-2 h-2 bg-primary-600 rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
              ))}

              {filteredOptions.length === 0 && (
                <div className="px-3 py-2 text-gray-500 text-center">
                  No se encontraron opciones
                </div>
              )}

              {/* Opción "Otro" */}
              {allowCustom && !showCustomInput && (
                <button
                  type="button"
                  onClick={() => handleOptionSelect('otro')}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-900 border-t border-gray-200 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Otro (especificar)
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default MedicalSelector;
